/**
 * PURPOSE: El Centro de Programación (FTR-0028 WG3, D-22/D-23).
 * RESPONSIBILITIES: La pantalla completa — cabecera con el contexto, filtros, conmutador de vistas,
 *   las cuatro vistas, la barra de acciones masivas y los paneles laterales.
 *
 *   POR QUÉ AQUÍ Y NO EN UNA RUTA PROPIA. El Centro trabaja siempre sobre un `(evento, deporte)`
 *   concreto, y en Winscore ese alcance lo da la página de deporte: `/campeonatos/:idevent/
 *   list-categorias/:idsport/deporte`. Se abre desde las acciones de Fases, como el resto de
 *   herramientas de preparación (planificar partidos, participantes, posiciones), y hereda el
 *   alcance verificado en vez de volver a resolverlo desde la URL. Es el patrón vigente y es lo que
 *   congela el FTR ("un `ProgrammingCenterModal`").
 *
 *   NADA DE ESTO AUTORIZA. El flag y el rol deciden si se OFRECE la pantalla; quien decide si se
 *   puede es el servidor, en cada petición. Por eso un 403 o un 404 del backend se muestran como
 *   estado propio y no como un fallo: el botón podía verse y aun así la respuesta ser "no".
 * COLLABORATORS: ProgrammingCenterProvider.jsx, las cuatro vistas, los paneles, FasesArea.jsx.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/ProgrammingCenterModal.jsx
 */
import React from "react";
import Modal from "@mui/joy/Modal";
import Sheet from "@mui/joy/Sheet";
import {
  FiAlertCircle,
  FiDatabase,
  FiLock,
  FiGrid,
  FiGitMerge,
  FiPlusCircle,
  FiRefreshCw,
  FiSettings,
  FiShuffle,
  FiSlash,
  FiUsers,
  FiX,
} from "react-icons/fi";

import ProgrammingCenterProvider, {
  VIEW_MODES,
  VIEW_OPTIONS,
  useProgrammingCenter,
} from "./ProgrammingCenterProvider.jsx";
import FiltersBar from "./components/FiltersBar.jsx";
import SelectionActionBar from "./components/SelectionActionBar.jsx";
import ExternalMatchesBanner from "./components/ExternalMatchesBanner.jsx";
import GroupView from "./views/GroupView.jsx";
import DateView from "./views/DateView.jsx";
import TimelineView from "./views/TimelineView.jsx";
import VenueView from "./views/VenueView.jsx";
import SetupPanel from "./panels/SetupPanel.jsx";
import MaterializePanel from "./panels/MaterializePanel.jsx";
import ReorganizePanel from "./panels/ReorganizePanel.jsx";
import ManualMatchPanel from "./panels/ManualMatchPanel.jsx";
import ParticipantsPanel from "./panels/ParticipantsPanel.jsx";
import FinalsPanel from "./panels/FinalsPanel.jsx";
import CourtsPanel from "./panels/CourtsPanel.jsx";
import { classifyAccessError } from "../../../utils/programmingCenterErrors.js";

const VIEWS = {
  [VIEW_MODES.GROUP]: GroupView,
  [VIEW_MODES.DATE]: DateView,
  [VIEW_MODES.TIMELINE]: TimelineView,
  [VIEW_MODES.VENUE]: VenueView,
};

const Stat = ({ label, value }) => (
  <span className="flex flex-col leading-tight">
    <span className="text-sm font-black text-slate-900">{value}</span>
    <span className="text-[10px] font-black uppercase tracking-wide text-slate-400">{label}</span>
  </span>
);

const EmptyState = ({ icon: Icon, title, description, action = null }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
    <Icon className="text-4xl text-slate-300" />
    <p className="text-lg font-black text-slate-800">{title}</p>
    <p className="max-w-md text-sm font-bold text-slate-500">{description}</p>
    {action}
  </div>
);

const Toolbar = ({ onClose }) => {
  const { context, draft, summary, view, setView, setPanel, refresh, fetching, hasDraft, graph } =
    useProgrammingCenter();

  const header = draft?.draft || null;
  const closed = context?.config?.isClosed;

  const toolButton = (key, label, Icon, { disabled = false, tone = "slate" } = {}) => (
    <button
      key={key}
      type="button"
      onClick={() => setPanel(key)}
      disabled={disabled}
      className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-black transition disabled:cursor-not-allowed disabled:opacity-45 ${
        tone === "green"
          ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          : tone === "amber"
            ? "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
            : "border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700"
      }`}
    >
      <Icon className="text-sm" />
      {label}
    </button>
  );

  return (
    <>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 bg-gradient-to-r from-white to-blue-50/70 px-4 py-3">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-600">
            Centro de Programación
          </p>
          <h2 className="truncate text-lg font-black text-slate-900">
            {context?.sport?.name || "Deporte"} · {context?.event?.name || "Evento"}
          </h2>
          <p className="text-[11px] font-bold text-slate-500">
            {header?.exists
              ? `Borrador v${draft?.version} · ${
                  header.status === "materialized" ? "materializado" : "en diseño"
                } · ${(header.group_sizes || []).join("·")} equipos por grupo`
              : "Sin fixture armado todavía"}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <Stat label="Partidos" value={summary.active - summary.byes} />
          <Stat label="BYE" value={summary.byes} />
          <Stat label="Materializados" value={summary.materialized} />
          <Stat label="Programados" value={summary.scheduled} />
          {summary.orphan ? <Stat label="Huérfanos" value={summary.orphan} /> : null}
          <button
            type="button"
            onClick={() => refresh({ scope: "structure" })}
            disabled={fetching}
            aria-label="Actualizar"
            title="Actualizar"
            className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 transition hover:border-blue-300 hover:text-blue-700 disabled:opacity-50"
          >
            <FiRefreshCw className={`text-sm ${fetching ? "animate-spin" : ""}`} />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar el Centro de Programación"
            className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 transition hover:border-rose-300 hover:text-rose-600"
          >
            <FiX className="text-sm" />
          </button>
        </div>
      </header>

      {closed ? (
        <p className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs font-black text-amber-800">
          <FiLock className="text-sm" />
          La configuración del deporte está cerrada: no se puede modificar el fixture hasta abrirla
          en Configuración.
        </p>
      ) : null}

      <ExternalMatchesBanner />

      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-2 print:hidden">
        <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1">
          {VIEW_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setView(option.value)}
              aria-pressed={view === option.value}
              className={`rounded-lg px-3 py-1.5 text-xs font-black transition ${
                view === option.value
                  ? "bg-white text-blue-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {toolButton("setup", hasDraft ? "Configuración" : "Armar fixture", FiSettings, {
            disabled: closed,
          })}
          {toolButton("manual", "Partido manual", FiPlusCircle, {
            disabled: closed || !hasDraft,
          })}
          {toolButton("participants", "Participantes", FiUsers, { disabled: !hasDraft })}
          {toolButton(
            "finals",
            graph?.counts?.conflicts
              ? `Fase final (${graph.counts.conflicts})`
              : "Fase final",
            FiGitMerge,
            {
              // El grafo solo existe sobre partidos reales; antes de materializar no hay nada que
              // recorrer y el botón prometería una pantalla vacía.
              disabled: !summary.materialized,
              tone: graph?.counts?.conflicts ? "amber" : "slate",
            }
          )}
          {toolButton("courts", "Canchas", FiGrid)}
          {toolButton("reorganize", "Reorganizar grupos", FiShuffle, {
            tone: "amber",
            disabled: closed || !hasDraft,
          })}
          {toolButton("materialize", "Materializar", FiDatabase, {
            tone: "green",
            disabled: closed || !hasDraft,
          })}
        </div>
      </div>

      <FiltersBar />
    </>
  );
};

const Body = () => {
  const { loading, error, hasDraft, setPanel, panel, view } = useProgrammingCenter();
  const View = VIEWS[view] || GroupView;

  if (error) {
    const kind = classifyAccessError(error);
    const copy = {
      forbidden: {
        title: "Sin acceso a este evento",
        description:
          "Tu rol o tu compañía no alcanzan este evento. El Centro de Programación es una herramienta de administración global.",
      },
      unavailable: {
        title: "El Centro no está disponible",
        description:
          "El módulo no está habilitado en este servidor, o el deporte no es colectivo.",
      },
      session: {
        title: "Sesión expirada",
        description: "Vuelve a iniciar sesión para continuar.",
      },
      error: {
        title: "No se pudo cargar el Centro",
        description: "Actualiza la vista; si continúa, revisa la conexión con el servidor.",
      },
    }[kind] || { title: "No se pudo cargar el Centro", description: "" };

    return <EmptyState icon={FiSlash} title={copy.title} description={copy.description} />;
  }

  if (loading) {
    return (
      <EmptyState
        icon={FiRefreshCw}
        title="Cargando el Centro de Programación"
        description="Leyendo el evento, el deporte, las sedes y el fixture."
      />
    );
  }

  if (!hasDraft) {
    return (
      <>
        <EmptyState
          icon={FiAlertCircle}
          title="Todavía no hay un fixture"
          description="Arma la estructura del torneo: equipos, grupos, rondas y fase final. Nada se escribe hasta que lo confirmes."
          action={
            <button
              type="button"
              onClick={() => setPanel("setup")}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-black text-white transition hover:bg-blue-700"
            >
              Armar fixture
            </button>
          }
        />
        {/* Sin borrador el flujo cae en la rama de arriba, pero "Armar fixture" (aquí y en la
            barra de herramientas) igual abre este panel: sin montarlo aquí, el backdrop de cierre
            de Shell aparece sobre un panel inexistente. */}
        <SetupPanel open={panel === "setup"} onClose={() => setPanel(null)} />
      </>
    );
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto bg-slate-50/70 p-4">
        <View />
      </div>
      <SelectionActionBar />

      <SetupPanel open={panel === "setup"} onClose={() => setPanel(null)} />
      <MaterializePanel open={panel === "materialize"} onClose={() => setPanel(null)} />
      <ReorganizePanel open={panel === "reorganize"} onClose={() => setPanel(null)} />
      <ManualMatchPanel open={panel === "manual"} onClose={() => setPanel(null)} />
      <ParticipantsPanel open={panel === "participants"} onClose={() => setPanel(null)} />
      <FinalsPanel open={panel === "finals"} onClose={() => setPanel(null)} />
      <CourtsPanel open={panel === "courts"} onClose={() => setPanel(null)} />
    </>
  );
};

const Shell = ({ onClose }) => {
  const { panel, setPanel } = useProgrammingCenter();
  return (
    <Sheet className="relative mx-auto flex max-h-[94vh] w-[min(98vw,1480px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white font-quickSand shadow-2xl">
      <Toolbar onClose={onClose} />
      <Body />
      {panel ? (
        // Un clic fuera del panel lo cierra sin cerrar el Centro: el contexto sigue a la vista.
        <button
          type="button"
          aria-label="Cerrar panel"
          onClick={() => setPanel(null)}
          className="absolute inset-0 z-20 cursor-default bg-slate-900/10"
        />
      ) : null}
    </Sheet>
  );
};

/**
 * @param {boolean} open
 * @param {boolean} enabled el flag del servidor ya resuelto por `useProgrammingCenterStatus`. No es
 *   una autorización: solo evita ofrecer una pantalla que el backend respondería con 404.
 */
const ProgrammingCenterModal = ({ open, onClose, idevent, idsport, enabled = true }) => (
  <Modal open={open} onClose={onClose} sx={{ p: 1.5 }}>
    <div className="h-full w-full">
      <ProgrammingCenterProvider idevent={idevent} idsport={idsport} enabled={open && enabled}>
        <Shell onClose={onClose} />
      </ProgrammingCenterProvider>
    </div>
  </Modal>
);

export default ProgrammingCenterModal;
